import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  svgContainer: {
    width: '100%',
  },
  empty: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    backgroundColor: '#1f2937',
    color: '#f9fafb',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    zIndex: 9999,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  legendSwatch: {
    width: '12px',
    height: '12px',
    borderRadius: '3px',
    flexShrink: 0,
  },
};

const DEFAULT_CLUSTER_COLORS = [
  '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed',
  '#db2777', '#0891b2', '#65a30d', '#ea580c', '#6366f1',
];

function collectLeaves(node) {
  if (!node.children || node.children.length === 0) {
    return [node];
  }
  const leaves = [];
  for (const child of node.children) {
    leaves.push(...collectLeaves(child));
  }
  return leaves;
}

function assignClusterColors(root, cutoff) {
  const colorMap = {};
  let colorIndex = 0;

  function walk(node) {
    if (node.data.distance <= cutoff && node.data.distance > 0) {
      const leaves = collectLeaves(node.data);
      const color = DEFAULT_CLUSTER_COLORS[colorIndex % DEFAULT_CLUSTER_COLORS.length];
      colorIndex++;
      for (const leaf of leaves) {
        colorMap[leaf.name] = color;
      }
      return;
    }

    if (!node.children || node.children.length === 0) {
      if (!colorMap[node.data.name]) {
        colorMap[node.data.name] = DEFAULT_CLUSTER_COLORS[colorIndex % DEFAULT_CLUSTER_COLORS.length];
        colorIndex++;
      }
      return;
    }

    for (const child of node.children) {
      walk(child);
    }
  }

  walk(root);
  return colorMap;
}

function getNodeColor(node, clusterColors, stateColors) {
  if (!node.children || node.children.length === 0) {
    const name = node.data.name;
    if (stateColors && stateColors[name]) return stateColors[name];
    if (clusterColors[name]) return clusterColors[name];
    return '#9ca3af';
  }

  const leaves = collectLeaves(node.data);
  const leafColors = leaves.map(l => {
    if (stateColors && stateColors[l.name]) return stateColors[l.name];
    return clusterColors[l.name] || '#9ca3af';
  });

  const uniqueColors = [...new Set(leafColors)];
  if (uniqueColors.length === 1) return uniqueColors[0];
  return '#9ca3af';
}

export default function StateClusterDendrogram({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const maxDistance = useMemo(() => {
    if (!data || !data.tree) return 0;
    function findMax(node) {
      let max = node.distance || 0;
      if (node.children) {
        for (const child of node.children) {
          max = Math.max(max, findMax(child));
        }
      }
      return max;
    }
    return findMax(data.tree);
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || !data || !data.tree) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 400;
    const margin = { top: 20, right: 100, bottom: 30, left: 40 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const root = d3.hierarchy(data.tree)
      .sum(d => (d.children && d.children.length > 0) ? 0 : 1);

    const clusterLayout = d3.cluster()
      .size([innerHeight, innerWidth]);

    clusterLayout(root);

    const distanceScale = d3.scaleLinear()
      .domain([0, maxDistance])
      .range([innerWidth, 0]);

    root.each(node => {
      node.y = distanceScale(node.data.distance || 0);
    });

    const cutoff = data.cutoff != null ? data.cutoff : maxDistance * 0.4;
    const stateColors = data.stateColors || {};
    const clusterColors = assignClusterColors(root, cutoff);

    const links = root.links();

    links.forEach(link => {
      const { source, target } = link;
      const linkColor = getNodeColor(target, clusterColors, stateColors);

      g.append('path')
        .attr('d', `M${target.y},${target.x}
                     H${source.y}
                     V${source.x}`)
        .attr('fill', 'none')
        .attr('stroke', linkColor)
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.7);
    });

    const cutoffX = distanceScale(cutoff);
    g.append('line')
      .attr('x1', cutoffX)
      .attr('y1', 0)
      .attr('x2', cutoffX)
      .attr('y2', innerHeight)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4');

    g.append('text')
      .attr('x', cutoffX)
      .attr('y', -6)
      .attr('text-anchor', 'middle')
      .attr('fill', '#6b7280')
      .attr('font-size', '10px')
      .text(`cutoff = ${cutoff.toFixed(1)}`);

    const leaves = root.leaves();
    leaves.forEach(leaf => {
      const name = leaf.data.name;
      const color = (stateColors && stateColors[name])
        ? stateColors[name]
        : (clusterColors[name] || '#6b7280');

      g.append('circle')
        .attr('cx', leaf.y)
        .attr('cy', leaf.x)
        .attr('r', 4)
        .attr('fill', color)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1)
        .style('cursor', 'pointer')
        .on('mouseenter', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            content: [
              `State: ${name}`,
              leaf.data.avgPrice != null ? `Avg Price: $${leaf.data.avgPrice.toFixed(2)}` : null,
              leaf.data.addressCount != null ? `Addresses: ${leaf.data.addressCount}` : null,
            ].filter(Boolean),
          });
        })
        .on('mousemove', (event) => {
          setTooltip(prev => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : null);
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });

      g.append('text')
        .attr('x', leaf.y + 8)
        .attr('y', leaf.x)
        .attr('dy', '0.35em')
        .attr('font-size', '11px')
        .attr('fill', '#374151')
        .attr('font-weight', 500)
        .text(name);
    });

    const internalNodes = root.descendants().filter(d => d.children && d.children.length > 0);
    internalNodes.forEach(node => {
      const nodeColor = getNodeColor(node, clusterColors, stateColors);

      g.append('circle')
        .attr('cx', node.y)
        .attr('cy', node.x)
        .attr('r', 3)
        .attr('fill', nodeColor)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1)
        .attr('opacity', 0.6)
        .style('cursor', 'pointer')
        .on('mouseenter', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            content: [
              `Merge Distance: ${(node.data.distance || 0).toFixed(2)}`,
              `Leaves: ${collectLeaves(node.data).length}`,
            ],
          });
        })
        .on('mousemove', (event) => {
          setTooltip(prev => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : null);
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    const axisScale = d3.scaleLinear()
      .domain([maxDistance, 0])
      .range([0, innerWidth]);

    const axis = d3.axisBottom(axisScale)
      .ticks(6)
      .tickFormat(d => d.toFixed(1));

    g.append('g')
      .attr('transform', `translate(0,${innerHeight + 8})`)
      .call(axis)
      .selectAll('text')
      .attr('fill', '#6b7280')
      .attr('font-size', '10px');

    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + 28)
      .attr('text-anchor', 'middle')
      .attr('fill', '#9ca3af')
      .attr('font-size', '10px')
      .text('Merge Distance');

  }, [data, maxDistance]);

  if (!data || !data.tree) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>State Pricing Clusters</h3>
        <p style={styles.subtitle}>Hierarchical clustering by pricing similarity</p>
        <div style={styles.empty}>No clustering data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>State Pricing Clusters</h3>
      <p style={styles.subtitle}>Hierarchical clustering by pricing similarity</p>

      <div style={styles.svgContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 400"
          width="100%"
          preserveAspectRatio="xMidYMid meet"
        />
      </div>

      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          {tooltip.content.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}
